import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar, User, Wrench, Pencil, Play, Pause, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatarMoeda } from '@/lib/currency';
import { getFrequenciaLabels, type RecorrenciaOS } from '@/types/recorrencia';
import { usePausarRecorrencia, useTerminarRecorrencia } from '@/hooks/useRecorrencias';
import { RecorrenciaEditDialog } from './RecorrenciaEditDialog';

interface RecorrenciaCardProps {
  recorrencia: RecorrenciaOS;
}

export function RecorrenciaCard({ recorrencia }: RecorrenciaCardProps) {
  const { t } = useTranslation();
  const pausar = usePausarRecorrencia();
  const terminar = useTerminarRecorrencia();
  const [dialogEditar, setDialogEditar] = useState(false);
  const [dialogTerminar, setDialogTerminar] = useState(false);

  const freqLabels = getFrequenciaLabels(t);
  const servicosLabel = recorrencia.servicos
    .map((s) => s.descricao || s.tipo)
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <div className="ow-card flex flex-col">
        {/* Header compacto: nome + status + frequência */}
        <div className="px-4 pt-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm leading-tight truncate">
              {recorrencia.cliente?.nome || recorrencia.cliente?.telefone || '—'}
            </h3>
            <span
              className={`status-pill ${
                recorrencia.ativa ? 'status-pill--ink' : 'status-pill--soft'
              }`}
              style={{ padding: '2px 8px', fontSize: 10 }}
            >
              <span
                className={`status-pill__led${
                  recorrencia.ativa ? ' status-pill__led--pulse' : ''
                }`}
                style={{ width: 6, height: 6 }}
                aria-hidden
              />
              {recorrencia.ativa
                ? t('recurrences.status.active')
                : t('recurrences.status.paused')}
            </span>
          </div>
          <div className="mt-1.5">
            <span className="status-pill" style={{ padding: '2px 8px', fontSize: 10 }}>
              {freqLabels[recorrencia.frequencia]}
            </span>
          </div>
        </div>

        {/* Body denso: 3 linhas info + valor inline */}
        <div className="px-4 pb-3 space-y-1.5 text-xs flex-1">
          {servicosLabel && (
            <div className="flex items-center gap-1.5">
              <Wrench className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{servicosLabel}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span>
              {format(new Date(recorrencia.proxima_data), "dd/MM 'às' HH:mm", { locale: ptBR })}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="truncate">
              {recorrencia.prestador?.profile
                ? recorrencia.prestador.profile.nome
                : t('recurrences.card.pool')}
            </span>
          </div>
          {recorrencia.valor_total > 0 && (
            <div className="text-sm font-bold tabular-nums pt-1">
              {formatarMoeda(recorrencia.valor_total)}
            </div>
          )}
        </div>

        {/* Footer compacto: ícones em vez de labels longos */}
        <div className="px-3 py-2 flex items-center gap-1 border-t border-border-soft">
          <button
            type="button"
            className="painel__icon-btn"
            onClick={() => pausar.mutate({ id: recorrencia.id, ativa: !recorrencia.ativa })}
            disabled={pausar.isPending}
            aria-label={
              recorrencia.ativa
                ? t('recurrences.card.pause')
                : t('recurrences.card.resume')
            }
            title={
              recorrencia.ativa
                ? t('recurrences.card.pause')
                : t('recurrences.card.resume')
            }
          >
            {recorrencia.ativa ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <button
            type="button"
            className="painel__icon-btn"
            onClick={() => setDialogEditar(true)}
            aria-label={t('recurrences.card.edit')}
            title={t('recurrences.card.edit')}
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            className="painel__icon-btn ml-auto"
            onClick={() => setDialogTerminar(true)}
            aria-label={t('recurrences.card.terminate')}
            title={t('recurrences.card.terminate')}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <RecorrenciaEditDialog
        open={dialogEditar}
        onOpenChange={setDialogEditar}
        recorrencia={recorrencia}
      />

      <AlertDialog open={dialogTerminar} onOpenChange={setDialogTerminar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recurrences.terminate.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('recurrences.terminate.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('recurrences.terminate.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => terminar.mutate(recorrencia.id)}
              disabled={terminar.isPending}
            >
              {t('recurrences.terminate.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
